import { camelCase } from 'lodash-es';
import YouTube, { YouTubeProps } from 'react-youtube';

import { Box, Heading, Text } from '@chakra-ui/react';
import { headerHeight } from '@udacity/udacity-header-v2';

import { Markdown } from '~/components/markdown';
import { SanityImage } from '~/components/sanity-image';

import {
  CaseStudyProcessStep as CaseStudyProcessStepType,
  CaseStudyProcessStepContentItem,
} from '../models/case-study-content';

import { TestimonialCard } from './testimonial-card';

export function ProcessStep({ processStep }: { processStep: CaseStudyProcessStepType }) {
  const sectionId = camelCase(`caseStudy${processStep.title}`);

  return (
    <Box as='section' id={sectionId} scrollMarginTop={headerHeight}>
      <Box>
        <Box>
          <Heading as='h3' color='blue.500' size='marketing-3xl'>
            {processStep.title}
          </Heading>

          <Heading as='p' color='blue.800' marginTop='8px' size='marketing-xl'>
            {processStep.description}
          </Heading>
        </Box>

        <Box display='flex' flexDirection='column' gap='48px' marginTop='24px'>
          {processStep.content.map((contentItem, index) => (
            <ContentItem key={index} stepContent={contentItem} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

const videoOptions: YouTubeProps['opts'] = {
  height: '408px',
  width: '100%',
  host: 'https://www.youtube-nocookie.com',
  playerVars: {
    rel: 0,
  },
};

function ContentItem({ stepContent }: { stepContent: CaseStudyProcessStepContentItem }) {
  function getCardColor(index: number) {
    if (index === 0) {
      return 'accent.lime';
    }

    if (index === 1) {
      return 'accent.sea-foam.500';
    }

    return 'purple.500';
  }

  if (stepContent._type === 'image') {
    return <SanityImage image={stepContent} style={{ borderRadius: '8px' }} />;
  }

  if (stepContent._type === 'testimonial') {
    return <TestimonialCard testimonial={stepContent} />;
  }

  if (stepContent._type === 'video') {
    return (
      <Box
        sx={{
          iframe: {
            borderRadius: '8px',
          },
        }}
      >
        <YouTube opts={videoOptions} title='hey' videoId={stepContent.youTubeVideoId} />
      </Box>
    );
  }

  if (stepContent._type === 'markdownObject') {
    return <Markdown markdownText={stepContent.text} />;
  }

  if (stepContent._type === 'caseStudyStepList') {
    return (
      <Box display='flex' flexDirection='column' gap='16px'>
        {stepContent.content.map((item, index) => (
          <Box
            key={item.title}
            backgroundColor='accent.sky.100'
            borderRadius='8px'
            display='flex'
            gap='32px'
            padding='16px 24px'
          >
            <Box color='blue.500' fontSize='40px' fontWeight='600' lineHeight='48px'>
              {(index + 1).toString()}
            </Box>

            <Box>
              <Heading as='p' color='blue.800' size='marketing-lg'>
                {item.title}
              </Heading>

              <Text marginTop='4px'>{item.text}</Text>
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (stepContent._type === 'caseStudyStepCardList') {
    return (
      <Box display='flex' flexWrap='wrap' gap='16px'>
        {stepContent.content.map((item, index) => (
          <Box
            key={item.title}
            backgroundColor='blue.800'
            borderRadius='8px'
            flexBasis={{ base: '100%', md: `calc((100% / 3) - 32px / 3)` }}
            padding=' 24px'
          >
            <Heading as='p' color={getCardColor(index)} size='marketing-4xl' textAlign='center'>
              {item.title}
            </Heading>

            <Text color='white' fontSize='12px' fontWeight='500' marginTop='4px' textAlign='center'>
              {item.text}
            </Text>
          </Box>
        ))}
      </Box>
    );
  }

  return <Box>hey</Box>;
}
